import App from 'antd/lib/app';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Typography from 'antd/lib/typography';
import { InputNumber } from 'antd/lib';
import { Col, Row } from 'antd/lib/grid';
import { PjmRegistrationDto } from 'src/domain/pjm/dto/PjmRegistrationDto';
import { UpdatePjmRegistrationLocationDto } from 'src/domain/pjm/dto/PjmLocationDto';
import { usePjmRegistrationLocationUpdateMutation } from 'src/domain/pjm/queries';
import { ZONE_DEP_MAP } from 'src/domain/pjm/values';

interface IPjmRegistrationLocationEditModalProps {
    value: PjmRegistrationDto | null;
    onClose: (saved?: boolean) => void;
}

interface LocationEditFormValues {
    pricingPoint: string | null;
    capacityLossFactor: number | null;
    energyLossFactor: number | null;
    peakLoadContribution: number | null;
    winterPeakLoad: number | null;
}

export const PjmRegistrationLocationEditModal = ({ value, onClose }: IPjmRegistrationLocationEditModalProps) => {
    const { notification } = App.useApp();
    const [form] = Form.useForm<LocationEditFormValues>();
    const updateMutation = usePjmRegistrationLocationUpdateMutation();

    if (!value?.location) return null;
    if (!value?.locationInfo) return null;

    const { locationInfo, location } = value;

    const initialValues: LocationEditFormValues = {
        pricingPoint: locationInfo?.pricingPoint ?? null,
        capacityLossFactor: locationInfo?.capacityLossFactor ?? null,
        energyLossFactor: locationInfo?.energyLossFactor ?? null,
        peakLoadContribution: locationInfo?.peakLoadContribution ?? null,
        winterPeakLoad: locationInfo?.winterPeakLoad ?? null,
    };

    let pricingPointOptions: { label: string; value: string }[] = [];

    const zone = locationInfo?.zone;
    if (zone) {
        /** available options for zone, pricingPoint and state depends on selected EDC name */
        const { pricingPoint } = ZONE_DEP_MAP[zone];
        pricingPointOptions = pricingPoint.map(it => ({ label: it, value: it }));
    }

    const onFormFinish = async () => {
        const formValues = await form.validateFields();

        const dto: UpdatePjmRegistrationLocationDto = {
            summaryInfo: {
                pricingPoint: formValues.pricingPoint ?? null,
            },
            profileInfo: {
                energyLossFactor: formValues.energyLossFactor ?? undefined,
                capacityLossFactor: formValues.capacityLossFactor ?? undefined,
                peakLoadContribution: formValues.peakLoadContribution ?? undefined,
            },
        };

        try {
            await updateMutation.mutateAsync({ uuid: value.uuid, dto });
            notification.success({ message: 'Location updated successfully' });
            onClose(true);
        } catch {
            notification.error({ message: 'Failed to update location' });
        }
    };

    return (
        <Modal
            title="Edit Location"
            className="pjm-registration-modal"
            open
            width="600px"
            destroyOnHidden
            maskClosable={false}
            keyboard={false}
            onCancel={() => onClose()}
            confirmLoading={updateMutation.isLoading}
            footer={[
                <Button key="edit-location-cancel" onClick={() => onClose(false)}>
                    Cancel
                </Button>,
                <Button key="edit-location-save" type="primary" loading={updateMutation.isLoading} onClick={form.submit}>
                    Save
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" initialValues={initialValues} onFinish={onFormFinish}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={<Typography.Text strong>Location ID</Typography.Text>}>
                            <Input value={location.id ?? ''} disabled />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label={<Typography.Text strong>Location Name</Typography.Text>}>
                            <Input value={location.summaryInfo?.name ?? ''} disabled />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="capacityLossFactor" label={<Typography.Text strong>Capacity Loss Factor</Typography.Text>}>
                            <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="energyLossFactor" label={<Typography.Text strong>Energy Loss Factor</Typography.Text>}>
                            <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="peakLoadContribution"
                            label={<Typography.Text strong>Peak Load Contribution (kW)</Typography.Text>}
                            rules={[{ required: true, message: 'Value is required!' }]}
                        >
                            <InputNumber min={0} max={9999999.999} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="winterPeakLoad" label={<Typography.Text strong>Winter Peak Load (kW)</Typography.Text>}>
                            <InputNumber style={{ width: '100%' }} disabled />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="pricingPoint" label={<Typography.Text strong>Pricing Point</Typography.Text>}>
                            <Select
                                showSearch
                                autoClearSearchValue
                                allowClear
                                optionFilterProp="label"
                                placeholder="Select Pricing Point"
                                options={pricingPointOptions}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};
